import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { Target, TrendingDown, TrendingUp, Minus, Dumbbell, Info, Clock, AlertCircle } from "lucide-react";

const LABEL = {
  reducir: { icon: TrendingDown, color: "text-[var(--mep-pink)]", bg: "bg-[rgba(232,61,132,0.08)]", border: "border-[var(--mep-pink)]/40", text: "Reducir" },
  aumentar: { icon: TrendingUp, color: "text-[var(--mep-gold)]", bg: "bg-[rgba(212,175,55,0.08)]", border: "border-[var(--mep-gold)]/40", text: "Aumentar" },
  mantener: { icon: Minus, color: "text-[var(--mep-text-muted)]", bg: "bg-[var(--mep-surface-2)]", border: "border-[var(--mep-border)]", text: "Mantener" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export default function MyPlan() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/fitness-plan").then((r) => setPlan(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <AppShell title="Mi plan personalizado"><div className="text-[var(--mep-text-muted)]">Cargando...</div></AppShell>;
  if (!plan) return <AppShell title="Mi plan personalizado"><div className="text-[var(--mep-text-muted)]">No se pudo cargar el plan.</div></AppShell>;

  const hasAnyMeasurement = Object.values(plan.last_known).some((v) => v !== null);
  const due = plan.due_for_measurements;

  return (
    <AppShell
      title="Mi plan personalizado"
      subtitle="Rango fitness orientativo según tu perfil y objetivo. Sin diagnósticos ni promesas de reducción localizada."
    >
      <div className="max-w-6xl space-y-6" data-testid="my-plan-page">
        {/* Measurement reminder */}
        {due && (
          <div
            className="mep-card p-5 border-l-4 border-[var(--mep-pink)] flex items-start gap-4"
            data-testid="measurement-reminder"
          >
            <div className="w-10 h-10 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)] flex-shrink-0">
              <Clock size={20} />
            </div>
            <div className="flex-1">
              <div className="text-eyebrow text-[var(--mep-pink)] mb-1">
                {hasAnyMeasurement ? "Toca actualizar medidas" : "Registra tus primeras medidas"}
              </div>
              <p className="text-sm text-[var(--mep-text-muted)]">
                {hasAnyMeasurement
                  ? `Tu última actualización fue hace ${plan.days_since_measurements ?? 30}+ días. Recomendamos medirte cada 30 días para ver tu evolución real.`
                  : "Añade cintura, abdomen, cadera, glúteo, muslo y cuádriceps en la sección de Progreso para activar tu plan personalizado."}
              </p>
              <Link
                to="/progreso#medidas"
                data-testid="go-to-measurements"
                className="btn-pink inline-block mt-3 px-4 py-2 text-sm"
              >
                Actualizar medidas
              </Link>
            </div>
          </div>
        )}

        {/* Targets by measurement */}
        <div className="mep-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-2xl font-light tracking-tight">Rango fitness orientativo</h3>
            <div className="text-eyebrow text-[var(--mep-gold)]">Tus medidas</div>
          </div>

          {!hasAnyMeasurement ? (
            <div className="p-6 rounded-lg bg-[var(--mep-elev)] border border-dashed border-[var(--mep-border)] text-sm text-[var(--mep-text-muted)]">
              Aún no has registrado medidas corporales. Ve a <Link to="/progreso#medidas" className="text-[var(--mep-pink)] underline">Mi progreso</Link> y añade tu primer registro para ver tu plan.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {plan.targets.map((t) => {
                const meta = LABEL[t.direction] || LABEL.mantener;
                const Icon = meta.icon;
                const bandLabel = (t.target_low_cm && t.target_high_cm)
                  ? `${t.target_low_cm} – ${t.target_high_cm} cm`
                  : (t.current_cm ? "—" : "Añade medida");
                const deltaLabel = t.delta_cm === null || t.delta_cm === undefined
                  ? null
                  : (t.delta_cm === 0 ? "sin cambio" : `${t.delta_cm > 0 ? "+" : ""}${t.delta_cm} cm`);
                return (
                  <div
                    key={t.key}
                    data-testid={`target-${t.key}`}
                    className={`p-4 rounded-lg border ${meta.border} ${meta.bg}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">{t.label}</div>
                      <div className={`flex items-center gap-1 text-xs uppercase tracking-[0.2em] ${meta.color}`}>
                        <Icon size={14} /> {meta.text}
                      </div>
                    </div>
                    <div className="flex items-baseline gap-3">
                      <div className="font-display text-2xl font-light">
                        {t.current_cm ? `${t.current_cm} cm` : <span className="text-[var(--mep-text-muted)] text-base">sin dato</span>}
                      </div>
                      {deltaLabel && (
                        <div className={`text-xs ${t.delta_cm < 0 ? "text-[var(--mep-pink)]" : t.delta_cm > 0 ? "text-[var(--mep-gold)]" : "text-[var(--mep-text-muted)]"}`}>
                          {deltaLabel}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-[var(--mep-text-muted)] mt-2">
                      Rango orientativo: <span className="text-[var(--mep-text)]">{bandLabel}</span>
                    </div>
                    <div className="text-xs text-[var(--mep-text-muted)] mt-1 italic leading-relaxed">
                      {t.reason}
                    </div>
                    {t.last_updated_at && (
                      <div className="text-[10px] text-[var(--mep-text-muted)] mt-2 uppercase tracking-[0.15em]">
                        Última medida: {fmtDate(t.last_updated_at)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recommended routines (from existing content_data) */}
        {plan.recommended_routines?.length > 0 && (
          <div className="mep-card p-6" data-testid="recommended-routines">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl font-light tracking-tight">Rutinas recomendadas</h3>
              <div className="text-eyebrow text-[var(--mep-pink)]">Para ti</div>
            </div>
            <p className="text-sm text-[var(--mep-text-muted)] mb-5">
              Ejercicios ya existentes en tu plan, priorizados según tu objetivo y tus medidas actuales.
            </p>
            <div className="space-y-4">
              {plan.recommended_routines.map((r) => (
                <div
                  key={r.muscle}
                  data-testid={`routine-${r.muscle}`}
                  className="p-4 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Dumbbell size={16} className="text-[var(--mep-pink)]" />
                    <div className="font-medium capitalize">{r.muscle}</div>
                  </div>
                  <div className="text-xs text-[var(--mep-text-muted)] mb-3">{r.reason}</div>
                  <ul className="text-sm space-y-1 ml-4 list-disc">
                    {r.exercises.map((ex) => (
                      <li key={ex.id}>
                        <span className="text-[var(--mep-text)]">{ex.name}</span>
                        <span className="text-[var(--mep-text-muted)]"> · {ex.sets}×{ex.reps} · {ex.level}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to={plan.profile.sex === "mujer" ? "/rutinas-mujeres" : "/rutinas-hombres"}
                className="btn-pink px-4 py-2 text-sm"
              >
                Ver todas las rutinas
              </Link>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="p-5 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]" data-testid="plan-disclaimer">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-[var(--mep-gold)] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[var(--mep-text-muted)] leading-relaxed">
              {plan.disclaimer}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
